import { useMemo, useState } from 'react';
import { crmStore, useCrm } from '../store';
import { Card } from '../ui';
import { Boxes, Plus, X, Pencil, Trash2, Loader2, Search, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from './ConfirmDialog';
import type { Produit } from '../types';

const eur = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

type Draft = {
  id?: string;
  categorie: string;
  libelle: string;
  description: string;
  coutHT: string;
  prixHT: string;
  unite: string;
  reference: string;
  sousFamille: string;
  tvaPct: number;
  fournisseur: string;
  actif: boolean;
};

const EMPTY: Draft = { categorie: '', libelle: '', description: '', coutHT: '', prixHT: '', unite: '', reference: '', sousFamille: '', tvaPct: 20, fournisseur: '', actif: true };

export function ProduitsCatalogue() {
  const data = useCrm();
  const confirm = useConfirm();
  const produits = (data.produits || []) as Produit[];
  const categories = (data.produitCategories || []) as string[];

  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState('');

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return produits.filter((p) => {
      if (catFilter && p.categorie !== catFilter) return false;
      if (!term) return true;
      return (
        p.libelle.toLowerCase().includes(term) ||
        (p.reference || '').toLowerCase().includes(term) ||
        (p.sousFamille || '').toLowerCase().includes(term) ||
        p.description.toLowerCase().includes(term) ||
        p.categorie.toLowerCase().includes(term)
      );
    });
  }, [produits, q, catFilter]);

  const grouped = useMemo(() => {
    const m = new Map<string, Produit[]>();
    filtered.forEach((p) => {
      const k = p.categorie || 'Sans catégorie';
      const arr = m.get(k) || [];
      arr.push(p);
      m.set(k, arr);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0], 'fr'));
  }, [filtered]);

  const save = async () => {
    if (!draft) return;
    if (!draft.libelle.trim()) {
      toast.error('Le libellé est obligatoire');
      return;
    }
    setSaving(true);
    try {
      await crmStore.saveProduit({
        id: draft.id,
        categorie: draft.categorie,
        libelle: draft.libelle,
        description: draft.description,
        coutHT: Number(draft.coutHT.replace(',', '.')) || 0,
        prixHT: Number(draft.prixHT.replace(',', '.')) || 0,
        unite: draft.unite,
        reference: draft.reference,
        sousFamille: draft.sousFamille,
        tvaPct: draft.tvaPct,
        fournisseur: draft.fournisseur,
        actif: draft.actif,
      });
      toast.success(draft.id ? 'Produit modifié' : 'Produit ajouté au catalogue');
      setDraft(null);
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || 'enregistrement impossible'}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p: Produit) => {
    const ok = await confirm({
      title: `Supprimer « ${p.libelle} » ?`,
      description: 'Le produit disparaît du catalogue. Les propositions déjà établies ne sont pas modifiées.',
      confirmLabel: 'Supprimer',
      destructive: true,
    });
    if (!ok) return;
    try {
      await crmStore.deleteProduit(p.id);
      toast.success('Produit supprimé');
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || 'suppression impossible'}`);
    }
  };

  const addCategorie = async () => {
    const v = newCat.trim();
    if (!v) return;
    try {
      await crmStore.addReferentiel('produit_categorie', v);
      setNewCat('');
      toast.success('Catégorie ajoutée');
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || 'ajout impossible'}`);
    }
  };

  const removeCategorie = async (c: string) => {
    const used = produits.some((p) => p.categorie === c);
    if (used) {
      toast.error('Catégorie utilisée par au moins un produit');
      return;
    }
    try {
      await crmStore.removeReferentiel('produit_categorie', c);
      toast.success('Catégorie supprimée');
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || 'suppression impossible'}`);
    }
  };

  const marge = (p: { coutHT: number; prixHT: number }) => {
    if (!p.prixHT) return null;
    return ((p.prixHT - p.coutHT) / p.prixHT) * 100;
  };

  return (
    <Card className="p-5 mb-6 border-teal-500/30">
      <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-teal-400">
        <Boxes size={14} strokeWidth={1.5} /> Catalogue produits, décorations &amp; locations
      </div>
      <p className="text-xs text-slate-400 leading-relaxed max-w-3xl mb-4">
        Tout ce qui n'est pas une prestation artistique&nbsp;: photobooth, mascottes, feu de jour, décorations,
        wedding planner, locations de matériel… Chaque produit dispose d'un coût d'achat et d'un prix de vente HT,
        et peut être ajouté à n'importe quelle proposition.
      </p>

      {/* Catégories */}
      <div className="mb-5 p-3 rounded-lg bg-slate-950/40 border border-slate-700/40">
        <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-slate-400">
          <Tag size={12} strokeWidth={1.5} /> Catégories ({categories.length})
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {categories.length === 0 && (
            <span className="text-[11px] text-slate-500 italic">Aucune catégorie — ajoutez-en une ci-dessous.</span>
          )}
          {categories.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800/60 border border-slate-700/50 text-[11px] text-slate-300"
            >
              {c}
              <button onClick={() => removeCategorie(c)} className="text-red-400 hover:text-red-300">
                <X size={11} strokeWidth={1.5} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCategorie()}
            placeholder="Nouvelle catégorie (ex : Décorations, Locations…)"
            className="flex-1 bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-1.5 text-xs text-slate-200 placeholder:text-slate-600"
          />
          <button
            onClick={addCategorie}
            className="px-3 py-1.5 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/40 text-teal-200 rounded-md text-xs font-semibold flex items-center gap-1"
          >
            <Plus size={12} strokeWidth={1.5} /> Ajouter
          </button>
        </div>
      </div>

      {/* Barre outils */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} strokeWidth={1.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un produit…"
            className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md pl-8 pr-2.5 py-1.5 text-xs text-slate-200 placeholder:text-slate-600"
          />
        </div>
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-1.5 text-xs text-slate-200"
        >
          <option value="">Toutes les catégories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button
          onClick={() => setDraft({ ...EMPTY, categorie: catFilter || categories[0] || '' })}
          className="px-3 py-1.5 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/40 text-teal-200 rounded-md text-xs font-semibold flex items-center gap-1"
        >
          <Plus size={13} strokeWidth={1.5} /> Nouveau produit
        </button>
      </div>

      {/* Liste */}
      {produits.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-6 text-center">
          Le catalogue est vide. Créez votre premier produit.
        </p>
      ) : (
        <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
          {grouped.map(([cat, items]) => (
            <div key={cat}>
              <div className="text-[10px] uppercase tracking-widest text-teal-300/70 mb-1.5">
                {cat} ({items.length})
              </div>
              <div className="space-y-1.5">
                {items.map((p) => {
                  const m = marge(p);
                  return (
                    <div
                      key={p.id}
                      className="flex items-start gap-3 bg-slate-950/50 rounded-md px-3 py-2 border border-slate-800/60"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-200 truncate">
                          {p.reference && <span className="font-mono text-teal-300 mr-1.5">{p.reference}</span>}
                          {p.libelle}
                          {p.actif === false && <span className="ml-1.5 text-[10px] text-slate-500">(archivé)</span>}
                        </div>
                        {(p.sousFamille || p.fournisseur) && (
                          <div className="text-[10px] text-slate-500">{[p.sousFamille, p.fournisseur && `Fournisseur : ${p.fournisseur}`].filter(Boolean).join(' · ')}</div>
                        )}
                        {p.description && (
                          <div className="text-[11px] text-slate-500 line-clamp-2 whitespace-pre-wrap break-words">
                            {p.description}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-semibold text-slate-100">{eur(p.prixHT)} HT</div>
                        <div className="text-[10px] text-slate-500">
                          Coût {eur(p.coutHT)}
                          {m != null && (
                            <span className={m >= 0 ? ' text-emerald-400' : ' text-red-400'}> · {m.toFixed(0)}&nbsp;%</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() =>
                            setDraft({
                              id: p.id,
                              categorie: p.categorie,
                              libelle: p.libelle,
                              description: p.description,
                              coutHT: String(p.coutHT ?? ''),
                              prixHT: String(p.prixHT ?? ''),
                              unite: p.unite,
                              reference: p.reference || '',
                              sousFamille: p.sousFamille || '',
                              tvaPct: p.tvaPct ?? 20,
                              fournisseur: p.fournisseur || '',
                              actif: p.actif !== false,
                            })
                          }
                          className="p-1 text-slate-400 hover:text-amber-300"
                          title="Modifier"
                        >
                          <Pencil size={13} strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => remove(p)}
                          className="p-1 text-slate-400 hover:text-red-300"
                          title="Supprimer"
                        >
                          <Trash2 size={13} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modale édition */}
      {draft && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDraft(null)}>
          <div
            className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-100">
                {draft.id ? 'Modifier le produit' : 'Nouveau produit'}
              </h3>
              <button onClick={() => setDraft(null)} className="text-slate-400 hover:text-slate-200">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Catégorie</label>
                <select
                  value={draft.categorie}
                  onChange={(e) => setDraft({ ...draft, categorie: e.target.value })}
                  className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200"
                >
                  <option value="">Sans catégorie</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Libellé *</label>
                <input
                  value={draft.libelle}
                  onChange={(e) => setDraft({ ...draft, libelle: e.target.value })}
                  placeholder="Ex : Photobooth 200 impressions"
                  className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200 placeholder:text-slate-600"
                />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">
                  Description (reprise dans la proposition)
                </label>
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={4}
                  placeholder="Ex : FEU DE JOUR — 8 canons / 8 fumigènes diurnes Ø 40 mm, multi couleur intense, 1 télécommande sans fil…"
                  className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200 placeholder:text-slate-600 resize-y"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Coût HT</label>
                  <input
                    value={draft.coutHT}
                    onChange={(e) => setDraft({ ...draft, coutHT: e.target.value })}
                    inputMode="decimal"
                    placeholder="0"
                    className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Prix vente HT *</label>
                  <input
                    value={draft.prixHT}
                    onChange={(e) => setDraft({ ...draft, prixHT: e.target.value })}
                    inputMode="decimal"
                    placeholder="0"
                    className="w-full bg-slate-950/60 border border-teal-700/50 rounded-md px-2.5 py-2 text-xs text-slate-100 font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Unité</label>
                  <input
                    value={draft.unite}
                    onChange={(e) => setDraft({ ...draft, unite: e.target.value })}
                    placeholder="jour, pièce…"
                    className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200 placeholder:text-slate-600"
                  />
                </div>
              </div>


              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Référence</label><input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} placeholder="Ex : PH400" className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200 placeholder:text-slate-600" /></div>
                <div><label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Sous-famille</label><input value={draft.sousFamille} onChange={(e) => setDraft({ ...draft, sousFamille: e.target.value })} placeholder="Ex : Photobooth" className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200 placeholder:text-slate-600" /></div>
                <div><label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Fournisseur</label><input value={draft.fournisseur} onChange={(e) => setDraft({ ...draft, fournisseur: e.target.value })} className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200 placeholder:text-slate-600" /></div>
                <div><label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">TVA</label>
                  <select value={draft.tvaPct} onChange={(e) => setDraft({ ...draft, tvaPct: Number(e.target.value) })} className="w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2.5 py-2 text-xs text-slate-200 placeholder:text-slate-600">
                    {[20, 10, 5.5, 0].map((t) => <option key={t} value={t}>{String(t).replace('.', ',')} %</option>)}
                  </select>
                </div>
              </div>
              {(() => { const v = Number(draft.prixHT.replace(',', '.')) || 0; return v ? <div className="text-[11px] text-slate-400">Prix TTC : <strong className="text-slate-200">{eur(v * (1 + draft.tvaPct / 100))}</strong></div> : null; })()}
              <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={draft.actif} onChange={(e) => setDraft({ ...draft, actif: e.target.checked })} className="accent-teal-500" /> Actif (décocher pour archiver sans supprimer)</label>
              {(() => {
                const c = Number(draft.coutHT.replace(',', '.')) || 0;
                const v = Number(draft.prixHT.replace(',', '.')) || 0;
                if (!v) return null;
                const m = ((v - c) / v) * 100;
                return (
                  <div className="text-[11px] text-slate-400 bg-slate-950/40 rounded-md px-3 py-2 border border-slate-800">
                    Marge&nbsp;: <strong className={m >= 0 ? 'text-emerald-400' : 'text-red-400'}>{eur(v - c)}</strong> soit{' '}
                    <strong className={m >= 0 ? 'text-emerald-400' : 'text-red-400'}>{m.toFixed(1)}&nbsp;%</strong>
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setDraft(null)}
                className="px-4 py-2 text-xs text-slate-300 hover:text-slate-100 rounded-md border border-slate-700"
              >
                Annuler
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/40 text-teal-200 rounded-md text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {saving && <Loader2 size={13} className="animate-spin" strokeWidth={1.5} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
